'use client';

import 'lib/yup-config';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { SuccessModal } from 'components/atomic/atoms';
import { Button, PhoneCodes } from 'components/atomic/nuclei';
import { HeaderOrganism } from 'components/atomic/organism';
import { NAME_VALIDATION_CONFIG, staticMediaStoreBaseURL } from 'config/common';
import phoneConfig from 'config/phone';
import phoneCountryCodes from 'config/phoneCountryCodes';
import { useIsMobile } from 'helpers/screenResolutionHelper';
import { captureGTMEventSuccessfullLeadForm } from 'lib/gtm';
import { getFormattedDate } from 'lib/time';
import { isNonEmptyPlainObject } from 'lib/utils';
import mobileNumber from 'lib/validations/mobile-number';
import { yupValidator } from 'lib/yup-validator';
import { postLeadCaptureFormData } from 'services/leadForm.service';

const { isMobileNumberValid } = mobileNumber;

const {
  firstCountryToShow,
  mobileBasicValidationFailureError,
  mobileInputError,
  mobileNotStartZeroRegex
} = phoneConfig;

export default function LeadCaptureForm(data) {
  const [isMobile] = useIsMobile();

  const leadFormTranslation = useTranslations('leadForm');
  const [direction, setDirection] = useState('ltr');
  const [showEventDetailsBlock, setShowEventDetailsBlock] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [inputs, setInputs] = useState({
    leadType: Number(data.leadType),
    howCanWeHelp: [],
    dialCode: phoneCountryCodes.AE.code,
    companyName: '',
    phone: '',
    typeOfEvent: '',
    eventDate: '',
    venueType: '',
    otherRequirements: '',
    expectedGuest: '',
    howCanWeReachYou: '',
    whenIsTheBestTimeToReachYou: '',
    typeOfWedding: ''
  });
  const [error, setError] = useState({});

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupValidator(
      yup.object().shape({
        fullName: yup
          .string()
          .required('Full name is mandatory!')
          .matches(
            NAME_VALIDATION_CONFIG.nameRegex,
            NAME_VALIDATION_CONFIG.nameInputError
          ),
        emailAddress: yup
          .string()
          .trim()
          .transform((val) => (val ? val.toLowerCase() : ''))
          .email('Invalid email format')
          .isIcannEmail('Invalid email domain or TLD')
          .required('Contact Email is required')
      })
    )
  });

  const formTexts = {
    heading: leadFormTranslation('heading'),
    fullName: leadFormTranslation('fullName'),
    companyName: leadFormTranslation('companyName'),
    emailAddress: leadFormTranslation('emailAddress'),
    phone: leadFormTranslation('phone'),
    eventType: leadFormTranslation('eventType'),
    howCanWeHelp: leadFormTranslation('howCanWeHelp'),
    prefferredTime: leadFormTranslation('prefferredTime'),
    preferredMode: leadFormTranslation('preferredMode'),
    morePreparedBlockHeading: leadFormTranslation('morePreparedBlockHeading'),
    shareEventDetailsHeading: leadFormTranslation('shareEventDetailsHeading'),
    eventDate: leadFormTranslation('eventDate'),
    venueType: leadFormTranslation('venueType'),
    expectedGuests: leadFormTranslation('expectedGuests'),
    otherRequirement: leadFormTranslation('otherRequirements'),
    bookNow: leadFormTranslation('bookNow'),
    typeOfWedding: leadFormTranslation('typeOfWedding'),
    typeOfWeddingPlaceholder: leadFormTranslation('typeOfWeddingPlaceholder'),
    prefferredTimePlaceholder: leadFormTranslation('prefferredTimePlaceholder'),
    preferredModePlaceholder: leadFormTranslation('preferredModePlaceholder'),
    venueTypePlaceholder: leadFormTranslation('venueTypePlaceholder')
  };

  const toggleEventDetailsBlock = () => {
    setShowEventDetailsBlock(!showEventDetailsBlock);
  };

  const handleChange = (e) => {
    e.persist();
    setInputs((prevInputs) => ({
      ...prevInputs,
      [e.target.name]: e.target.value
    }));
    const errorsData = delete error[e.target.name];
    setError({ ...errorsData });
  };

  const renderEventTypeDiv = (inputText, marginValue, renderOptions) => (
    <div className='mb-7 md:mb-10'>
      <div
        className={`border-b ${
          error.typeOfEvent ? 'border-cinnabar' : 'border-white'
        } w-full`}
      >
        <label
          htmlFor={inputText}
          className='text-white text-base md:text-xl font-normal leading-5 font-Montserrat pb-2 block'
        >
          {inputText}
        </label>

        <div className='relative flex w-full flex-row items-center justify-center pe-2 md:pe-4'>
          <select
            className={`relative cursor-pointer h-10 focus:outline-none appearance-none outline-none w-full text-sm md:text-lg font-normal leading-5 font-Montserrat bg-transparent pb-2 block ${
              inputs.typeOfEvent ? 'not-italic' : 'italic'
            } ${error.typeOfEvent ? 'text-cinnabar' : 'text-white'}`}
            name='typeOfEvent'
            onChange={handleChange}
          >
            <option
              value=''
              className={
                'cursor-pointer text-sm md:text-lg font-normal italic leading-5 font-Montserrat block text-black'
              }
            >
              Select from the list
            </option>

            {renderOptions.length &&
              renderOptions.map((option) => (
                <option
                  className='cursor-pointer outline-none w-full text-sm md:text-lg font-normal leading-5 font-Montserrat text-black block'
                  key={option.id}
                  value={option.name}
                >
                  {option.name}
                </option>
              ))}
          </select>

          <Image
            loading='eager'
            src={`${staticMediaStoreBaseURL}/icons/down-arrow-white.svg`}
            height={12}
            width={12}
            alt='DownArrow'
          />
        </div>
      </div>
    </div>
  );

  const renderDropDownOptions = (
    inputText,
    marginValue,
    fieldName,
    renderOptions,
    placeholder
  ) => (
    <>
      {
        // TODO: Group the conditionals.
      }
      <div
        className={`border-b ${
          error[fieldName] ? 'border-cinnabar' : 'border-white'
        } w-full mb-7 ${
          inputText === 'When is the best time to reach you?'
            ? 'md:mb-0'
            : 'md:mb-10'
        }`}
      >
        <label
          htmlFor={inputText}
          className={`text-white ${
            inputText === 'Venue Type'
              ? 'text-sm md:text-lg'
              : 'text-base md:text-xl'
          } font-normal leading-5 font-Montserrat pb-2 block`}
        >
          {inputText}
        </label>

        <div className='relative flex w-full flex-row items-center justify-center pe-2 md:pe-4'>
          <select
            className={`${
              inputs[fieldName] ? 'not-italic' : 'italic'
            } w-full focus:outline-none appearance-none outline-none text-sm md:text-lg font-normal leading-5 font-Montserrat bg-transparent   ${
              error[fieldName] ? 'text-cinnabar' : 'text-white'
            } pb-2 block`}
            name={fieldName}
            onChange={handleChange}
          >
            <option
              value={''}
              className={`w-full text-sm md:text-lg font-normal leading-5 italic font-Montserrat bg-transparent text-black pb-2 block  ${
                error[fieldName] ? 'text-cinnabar' : 'text-white'
              }`}
            >
              {placeholder}
            </option>

            {renderOptions &&
              renderOptions.map((option, index) => (
                <option
                  key={index}
                  className='w-full text-sm md:text-lg font-normal leading-5 font-Montserrat bg-transparent text-black pb-2 block'
                  value={option}
                >
                  {option}
                </option>
              ))}
          </select>

          <Image
            loading='eager'
            src={`${staticMediaStoreBaseURL}/icons/down-arrow-white.svg`}
            height={12}
            width={12}
            alt='DownArrow'
          />
        </div>
      </div>
      <div className='text-red-700'>{error[fieldName]}</div>
    </>
  );
  const handleCheckbox = (fieldName) => (e) => {
    e.persist();
    if (e.target.checked) {
      setInputs((prevInputs) => ({
        ...prevInputs,
        [fieldName]: [...prevInputs[fieldName], e.target.value]
      }));
    } else {
      const howCanWeHelpArray =
        inputs[fieldName].length &&
        inputs[fieldName].filter((value) => e.target.value !== value);
      setInputs((prevInputs) => ({
        ...prevInputs,
        [fieldName]: howCanWeHelpArray
      }));
    }
  };
  const NameComponent = ({ fieldName, option }) => (
    <div
      className={`relative self-start w-4.5 h-4.5 rounded-full bg-white border border-opacity-0 box-border
    mt-1 flex-shrink-0 flex justify-center items-stretch`}
    >
      <Image
        onClick={() =>
          handleCheckbox(fieldName)({
            target: { value: option.name, checked: false },
            persist: () => {}
          })
        }
        loading='eager'
        src={`${staticMediaStoreBaseURL}/icons/check-pink.svg`}
        alt='check'
        height={10}
        width={10}
      />
    </div>
  );
  const WithoutNameComponent = ({ fieldName, option }) => (
    <div
      onClick={() =>
        handleCheckbox(fieldName)({
          target: { value: option.name, checked: true },
          persist: () => {}
        })
      }
      className={
        'self-start w-4.5 h-4.5 rounded-full border border-white box-border mt-1 flex-shrink-0 flex flex-col'
      }
    ></div>
  );
  const LabelsComponent = ({ option }) => (
    <div className='flex w-full ms-4'>
      <label
        htmlFor={option.name}
        className='text-white text-sm md:text-lg font-normal leading-5 font-Montserrat block'
      >
        {option.name}
        {option.examples ? (
          <span className='text-white text-sm md:text-lg font-normal leading-5'>
            {option.examples}
          </span>
        ) : (
          ''
        )}
      </label>
    </div>
  );
  const renderCheckboxBlock = (
    inpuText,
    marginValue,
    fieldName,
    renderOptions
  ) => (
    <div className='md:mb-12 mb-7 w-full'>
      <div
        className={
          'text-white text-base md:text-xl font-normal leading-5 font-Montserrat pb-2 block'
        }
      >
        {inpuText}
      </div>

      <div className='flex flex-wrap flex-col md:flex-row self-start items-start'>
        {renderOptions.map((option, index) => (
          <div
            className='md:w-1/2 pb-2'
            key={index}
          >
            {Object.values(option).length ? (
              <div
                className='flex self-start'
                key={index}
              >
                <input
                  className='hidden'
                  id={option.name}
                  type='checkbox'
                  value={option.name}
                  name={option.name}
                  onClick={handleCheckbox(fieldName)}
                  autoComplete='off'
                />
                {inputs[fieldName].includes(option.name) ? (
                  <NameComponent {...{ fieldName, option }} />
                ) : (
                  <WithoutNameComponent {...{ fieldName, option }} />
                )}
                <LabelsComponent
                  {...{
                    option
                  }}
                />
              </div>
            ) : (
              ''
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const handleDateChange = (date) => {
    let dateWithOffset = getFormattedDate({
      date: new Date(date),
      format: 'YYYY-MM-DD'
    });
    const offset = new Date().getTimezoneOffset();
    dateWithOffset = `${dateWithOffset} 00:00${offset}`;
    const eventDate = new Date(dateWithOffset).getTime();
    setInputs((prevInputs) => ({ ...prevInputs, eventDate }));
  };

  const checkForSecondValidation = ({ nameValue, secondValidation }) =>
    error[nameValue] || error === secondValidation;

  const renderInputBox = (
    fieldName,
    marginValue,
    nameValue,
    inputType,
    placeholder,
    isRequired,
    secondValidation
  ) => (
    <div className={marginValue}>
      <div
        className={`border-b w-full transition-colors duration-700 delay-150 ease-in ${
          checkForSecondValidation({ nameValue, secondValidation })
            ? 'border-cinnabar '
            : 'border-white'
        }`}
      >
        <label
          htmlFor={fieldName}
          className='text-white text-base md:text-xl font-normal leading-5 font-Montserrat pb-2 block'
        >
          {fieldName}
          {isRequired ? '*' : ''}
        </label>

        <input
          id={fieldName}
          type={inputType}
          onChange={handleChange}
          value={inputs[fieldName]}
          placeholder={placeholder}
          name={nameValue}
          // TODO: Group conditionals.
          className={`cursor-pointer outline-none w-full text-sm md:text-lg font-normal leading-5 font-Montserrat bg-transparent ${
            error[nameValue] || error[secondValidation]
              ? 'placeholder-brand'
              : 'placeholder-white'
          } ${error[nameValue] ? 'text-cinnabar' : 'text-white'} pb-2 block`}
          required={false}
          autoComplete='off'
        />
      </div>
      {error[secondValidation] && (
        <div className='text-xs md:text-sm font-normal leading-5 font-Montserrat text-cinnabar italic slide-down'>
          Enter Valid Email Address
        </div>
      )}
    </div>
  );
  // TODO: make this a component
  // TODO: use object spread syntax
  const renderInputBoxWithYup = (
    fieldName,
    marginValue,
    nameValue,
    placeholder,
    isRequired,
    secondValidation,
    inputErrorMessage
  ) => (
    <>
      <div className={marginValue}>
        <div
          className={`border-b w-full transition-colors duration-700 delay-150 ease-in ${
            checkForSecondValidation({ nameValue, secondValidation })
              ? 'border-cinnabar '
              : 'border-white'
          }`}
        >
          <label
            htmlFor={fieldName}
            className='text-white text-base md:text-xl font-normal leading-5 font-Montserrat pb-2 block'
          >
            {fieldName}
            {isRequired ? '*' : ''}
          </label>
          <input
            id={fieldName}
            {...register(nameValue)}
            value={inputs[fieldName]}
            placeholder={placeholder}
            className={`cursor-pointer outline-none w-full text-sm md:text-lg font-normal leading-5 font-Montserrat bg-transparent ${
              errors[nameValue] || errors[nameValue]
                ? 'placeholder-brand'
                : 'placeholder-white'
            } ${errors[nameValue] ? 'text-cinnabar' : 'text-white'} pb-2 block`}
            autoComplete='off'
          />
        </div>
        {errors[nameValue] && (
          <div className='text-xs md:text-sm font-normal leading-5 font-Montserrat text-cinnabar italic slide-down'>
            {inputErrorMessage}
          </div>
        )}
      </div>
    </>
  );

  // eslint-disable-next-line no-unused-vars
  const changeDirection = () => {
    setDirection((prevState) => {
      if (prevState === 'rtl') {
        setDirection('ltr');
      } else {
        setDirection('rtl');
      }
    });
  };

  const handleKeyPress = (event) => {
    const keyCode = event.keyCode || event.which;
    const keyValue = String.fromCharCode(keyCode);
    if (!/^[0-9\b]+$/.test(keyValue)) {
      event.preventDefault();
    } else {
      setInputs((prevInputs) => ({
        ...prevInputs,
        [event.target.name]: event.target.value
      }));
      setError((err) => ({ ...err, [event.target.name]: '' }));
    }
  };

  const renderPhoneNumber = (fieldName, marginValue, nameValue, isRequired) => (
    <div className='mb-7 md:mb-10'>
      <div className='w-full'>
        <label
          htmlFor={fieldName}
          className='text-white text-base md:text-xl font-normal leading-5 font-Montserrat pb-2 block'
        >
          {fieldName}
          {isRequired ? '*' : ''}
        </label>

        <div className='flex flex-row justify-start md:justify-between'>
          <div
            className={
              'border-b w-20 text-white phone-code-container me-4 transition-colors duration-700 delay-150 ease-in'
            }
          >
            <PhoneCodes
              codeFirst
              onSelect={(code) =>
                handleChange({
                  target: { name: 'dialCode', value: code },
                  persist: () => {}
                })
              }
              showFirst={firstCountryToShow}
              defaultValue={inputs.dialCode}
              id='dialCode'
              name='dialCode'
              className='outline-none w-full text-sm md:text-lg font-medium leading-5 font-Montserrat bg-transparent placeholder-white pb-2 block'
            />
          </div>

          <div
            className={`border-b ${
              error.phone ? 'border-cinnabar' : 'border-white'
            } w-full`}
          >
            <input
              type='tel'
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              value={inputs[fieldName]}
              name={nameValue}
              placeholder={'Enter Phone Number'}
              autoComplete='off'
              className={`cursor-pointer outline-none w-full text-sm md:text-lg font-normal leading-5 font-Montserrat bg-transparent ${
                error[nameValue] ? 'placeholder-brand' : 'placeholder-white'
              } ${
                error[nameValue] ? 'text-cinnabar' : 'text-white'
              } pb-2 block transform transition-colors`}
            />
          </div>
        </div>
        <div className='font-light text-lg text-white'>{error.phone}</div>
      </div>
    </div>
  );

  const isValidForm = () => {
    // const patternForPhone = new RegExp(/^[0-9\b]+$/);
    const newErrorData = {};

    if (!mobileNotStartZeroRegex.test(`${inputs.phone}`)) {
      newErrorData.phone = mobileBasicValidationFailureError;
    } else if (!isMobileNumberValid(`${inputs.dialCode}${inputs.phone}`)) {
      newErrorData.phone = mobileInputError;
    }

    if (!inputs.typeOfEvent) {
      newErrorData.typeOfEvent = true;
    }

    if (isNonEmptyPlainObject(newErrorData)) {
      setError((prevState) => ({ ...prevState, ...newErrorData }));
      return false;
    }
    return true;
  };
  const handleFormSuccess = () => {
    setIsSubmitted(true);
  };
  const submitClick = async (event) => {
    if (event) {
      if (isValidForm()) {
        const formData = {
          ...inputs,
          fullName: event.fullName,
          emailAddress: event.emailAddress
        };
        formData.phone = {
          dialCode: `${inputs.dialCode}`,
          number: inputs.phone
        };
        delete formData.dialCode;
        const formSumission = await postLeadCaptureFormData(formData);
        if (formSumission && formSumission.status) {
          captureGTMEventSuccessfullLeadForm();
          handleFormSuccess();
        }
      }
    }
  };
  const handlePageRedirect = () => {
    const router = useRouter();
    router.push(data.redirectTo);
  };
  return (
    <>
      <HeaderOrganism />
      <div
        dir={direction}
        className='md:min-h-leadFormHeight bg-leadform-gradient lead-capture'
      >
        <div className='h-full block w-full'>
          <div className='container mx-auto p-5 md:py-8 md:px-0 md:pb-8'>
            <div className='mb-6 md:mb-8 max-w-36 md:max-w-full'>
              <h2 className='text-xl md:text-45px text-white leading-7 md:leading-12.5 uppercase font-brandon pb-1.5 md:pb-2.5'>
                {formTexts.heading}
              </h2>
              <div className='flex justify-start'>
                {isMobile ? (
                  <Image
                    className='max-w-36 md:max-w-80'
                    width={309}
                    height={10}
                    // TODO: Access to process.env should be out of function calls.
                    src={`${staticMediaStoreBaseURL}/icons/line-mob.svg`}
                    alt='line'
                  />
                ) : (
                  <Image
                    width={629}
                    height={11}
                    src={`${staticMediaStoreBaseURL}/icons/line-desktop.svg`}
                    alt='line'
                  />
                )}
              </div>
            </div>

            <form
              id='lead-capture'
              className='block md:flex flex-row justify-between'
            >
              <div className='md:w-1/2 md:me-20'>
                {renderInputBoxWithYup(
                  formTexts.fullName,
                  'mb-5 md:mb-8',
                  'fullName',
                  'Enter your Full Name',
                  true,
                  'nameNotValid',
                  NAME_VALIDATION_CONFIG.nameInputError
                )}
                {data.parentId !== '2'
                  ? renderInputBox(
                      formTexts.companyName,
                      'mb-5 md:mb-8',
                      'companyName',
                      'text',
                      'Enter company name',
                      data.parentId === '5'
                    )
                  : ''}
                {
                  // TODO: Render as a component
                }
                {renderInputBoxWithYup(
                  formTexts.emailAddress,
                  'mb-5 md:mb-8',
                  'emailAddress',
                  'Enter your Email address',
                  true,
                  'emailNotValid',
                  'Enter Valid Email Address'
                )}
                {renderPhoneNumber(formTexts.phone, 'mb-8', 'phone', true)}
                {renderEventTypeDiv(
                  formTexts.eventType,
                  'mb-2',
                  data.eventTypes
                )}
                {data.weddingType && data.weddingType.length
                  ? renderDropDownOptions(
                      formTexts.typeOfWedding,
                      'mb-2',
                      'typeOfWedding',
                      data.weddingType,
                      formTexts.typeOfWeddingPlaceholder
                    )
                  : ''}
                {renderDropDownOptions(
                  formTexts.prefferredTime,
                  'mb-2',
                  'whenIsTheBestTimeToReachYou',
                  data.workingHours,
                  formTexts.prefferredTimePlaceholder
                )}
              </div>

              <div className='md:w-1/2'>
                {renderCheckboxBlock(
                  formTexts.howCanWeHelp,
                  'mb-2',
                  'howCanWeHelp',
                  data.helpOptions
                )}

                {renderDropDownOptions(
                  formTexts.preferredMode,
                  'mb-2',
                  'howCanWeReachYou',
                  data.communicationModes,
                  formTexts.preferredModePlaceholder
                )}

                <div className='mt-7 md:mt-10 w-full mb-12 md:mb-0'>
                  <label
                    htmlFor={formTexts.morePreparedBlockHeading}
                    className='text-white text-base md:text-xl font-normal leading-5 font-Montserrat pb-2 block'
                  >
                    {formTexts.morePreparedBlockHeading}
                  </label>

                  <div className='p-4 border border-white rounded-xl'>
                    <div className='flex flex-row justify-center items-center'>
                      <div
                        className='outline-none w-full text-sm md:text-lg italic font-normal leading-5
                         font-Montserrat text-white pb-2 block cursor-pointer'
                        onClick={() => toggleEventDetailsBlock()}
                      >
                        {formTexts.shareEventDetailsHeading}
                      </div>

                      <a
                        onClick={() => toggleEventDetailsBlock()}
                        className='border-none cursor-pointer max-w-max'
                      >
                        <Image
                          loading='eager'
                          src={`${staticMediaStoreBaseURL}/icons/down-arrow-white.svg`}
                          height={12}
                          width={12}
                          alt='DownArrow'
                        />
                      </a>
                    </div>
                    {showEventDetailsBlock ? (
                      <div className='slide-down mt-2 md:mt-5'>
                        <div className='border-b border-white mb-7 md:mb-10'>
                          <div className='text-white text-sm md:text-lg font-normal leading-5 font-Montserrat pb-2 block'>
                            {formTexts.eventDate}
                          </div>

                          <div className='flex space-x-2 space-y-2'>
                            <Image
                              src={`${staticMediaStoreBaseURL}/icons/calendar.svg`}
                              height={20}
                              width={20}
                              alt='calendar'
                            />

                            <ReactDatePicker
                              onChange={handleDateChange}
                              name='eventDate'
                              dateFormat='dd/MM/yyyy'
                              selected={inputs.eventDate}
                              className='outline-none w-full text-sm md:text-base font-normal rounded for-ios bg-transparent
                                placeholder-white text-white appearance-none pb-2 block focus:text-white active:text-white'
                              minDate={new Date()}
                              placeholderText='Select event date'
                              value={inputs.eventDate}
                              scrollableYearDropdown={true}
                              showMonthDropdown={true}
                              showYearDropdown={true}
                            />
                          </div>
                        </div>

                        {renderDropDownOptions(
                          formTexts.venueType,
                          'mb-2',
                          'venueType',
                          data.venueTypes,
                          formTexts.venueTypePlaceholder
                        )}

                        <div className='border-b border-white mb-7 md:mb-10'>
                          <div className='text-white text-sm md:text-lg font-normal leading-5 font-Montserrat block'>
                            {formTexts.expectedGuests}
                          </div>

                          <input
                            type='number'
                            className='outline-none w-full text-sm md:text-base font-normal rounded
                            for-ios bg-transparent placeholder-white text-white appearance-none block pb-2'
                            onChange={handleChange}
                            value={inputs.expectedGuest}
                            name={'expectedGuest'}
                            autoComplete='off'
                            placeholder='Enter no of guest'
                          />
                        </div>

                        <div className='border-b border-white mb-1'>
                          <div className='text-white text-sm md:text-lg font-normal leading-5 font-Montserrat block'>
                            {formTexts.otherRequirement}
                          </div>

                          <input
                            type='text'
                            className='outline-none w-full text-sm md:text-base font-normal rounded for-ios
                            bg-transparent placeholder-white text-white appearance-none block pb-2'
                            onChange={handleChange}
                            value={inputs.otherRequirements}
                            name={'otherRequirements'}
                            autoComplete='off'
                            placeholder='Type here'
                          />
                        </div>
                      </div>
                    ) : (
                      ''
                    )}
                  </div>
                </div>
                <div
                  className={`fixed bottom-0 right-0 left-0 md:static md:flex md:justify-end w-full py-2 px-5 md:p-0 ${
                    isMobile ? 'bg-leadform-gradient' : ''
                  }`}
                >
                  <Button
                    className='bg-white h-12.5 py-4 rounded-md text-black uppercase text-base md:text-lg font-brandon leading-5
                      w-full md:max-w-52 mt-0 md:mt-9 block md:ml-auto'
                    onClick={handleSubmit(submitClick)}
                  >
                    {formTexts.bookNow}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
        {isSubmitted && (
          <SuccessModal
            open={isSubmitted}
            heading={'Thank you!'}
            message={
              'Your request has been placed. Our team will reach out to you soon.'
            }
            buttonText={'Continue'}
            handleClick={handlePageRedirect}
          />
        )}
      </div>
    </>
  );
}
